import {
  type AnyKeyHasher,
  type KeyHasher,
  type KeyHasherKeyType,
  type KeyHasherPatternType,
  ArrayKeyHasher,
} from "@repo/lib/keyHasher";
import { Observable, type Subscriber } from "rxjs";

type Key<H extends AnyKeyHasher> = KeyHasherKeyType<H>;
type Pattern<H extends AnyKeyHasher> = KeyHasherPatternType<H>;

export interface ObservableCacheOptions<H extends AnyKeyHasher> {
  keyHasher?: H;
  onInitialSubscribe?: (key: Key<H>) => void;
  onFinalUnsubscribe?: (key: Key<H>) => void;
}

/** Handles caching and serving observables and subscriptions.
 *
 * Operates similarly to an EventTarget, but by using observables:
 * - `observable(key)` acts like `addListener(eventType)`
 * - `publish(key, data)` acts like `emit(eventType, eventData)`
 * */
export class ObservableCache<H extends KeyHasher<Key<H>, Pattern<H>>> {
  #hasher: H;
  #observables = new Map<string, Set<Subscriber<unknown>>>();

  constructor({
    keyHasher = new ArrayKeyHasher() as H,
    onInitialSubscribe,
    onFinalUnsubscribe,
  }: ObservableCacheOptions<H> = {}) {
    this.#hasher = keyHasher;
    this.onInitialSubscribe = onInitialSubscribe;
    this.onFinalUnsubscribe = onFinalUnsubscribe;
  }

  /** A handler called when the first subscriber for a key is registered (e.g., for setup). */
  onInitialSubscribe?: (key: Key<H>) => void;
  /** A handler called when the last subscriber for a key unsubscribes (e.g., for teardown). */
  onFinalUnsubscribe?: (key: Key<H>) => void;

  /** Get the set of subscribers for the given serialized key. */
  private getSubscribers(strKey: string): Set<Subscriber<unknown>> | undefined {
    return this.#observables.get(strKey);
  }

  /** Add a subscriber for the given serialized key. */
  private addSubscriber(
    key: Key<H>,
    strKey: string,
    subscriber: Subscriber<unknown>
  ) {
    let first = false;
    let set = this.#observables.get(strKey);
    if (!set) {
      set = new Set();
      this.#observables.set(strKey, set);
      first = true;
    }
    set.add(subscriber);
    if (first) this.onInitialSubscribe?.(key);
  }

  /** Remove a subscription for the given serialized key. */
  private removeSubscriber(
    key: Key<H>,
    strKey: string,
    subscriber: Subscriber<unknown>
  ) {
    const set = this.#observables.get(strKey);
    if (set) {
      set.delete(subscriber);
      if (!set.size) {
        this.#observables.delete(strKey);
        this.onFinalUnsubscribe?.(key);
      }
    }
  }

  /** Get all keys with subscribers. */
  getAllKeys() {
    const keys: Key<H>[] = [];

    for (const strKey of this.#observables.keys()) {
      // deserialize and push the key
      const key = this.#hasher.unhash(strKey);
      keys.push(key);
    }

    return keys;
  }

  /** Get the keys for subscribers that match the given pattern. Uses the key hasher's match
   * test for filtering. */
  getMatchingKeys(pattern: Pattern<H>): Key<H>[] {
    const keys: Key<H>[] = [];
    for (const strKey of this.#observables.keys()) {
      const key = this.#hasher.unhash(strKey);
      if (this.#hasher.matches(key, pattern)) {
        keys.push(key);
      }
    }
    return keys;
  }

  /** Publish the given data for the key, so that all observers receive the update. */
  publish(key: Key<H>, data: unknown) {
    const strKey = this.#hasher.hash(key);
    const set = this.getSubscribers(strKey);
    if (set) {
      for (const subscription of set) {
        subscription.next(data);
      }
    }
  }

  /** Send an error signal to all subscribers for the given key. */
  error(key: Key<H>, error: unknown) {
    const strKey = this.#hasher.hash(key);
    const set = this.getSubscribers(strKey);
    if (set) {
      for (const subscription of set) {
        subscription.error(error);
      }
    }
  }

  /** Send a complete signal to all subscribers for the given key. */
  complete(key: Key<H>) {
    const strKey = this.#hasher.hash(key);
    const set = this.getSubscribers(strKey);
    if (set) {
      for (const subscription of set) {
        subscription.complete();
      }
    }
  }

  /** Checks if observables exist for the given key. */
  has(key: Key<H>) {
    const strKey = this.#hasher.hash(key);
    return this.#observables.has(strKey);
  }

  /** Create an observable for the given key. */
  observable<T>(
    key: Key<H>,
    subscribe: (subscriber: Subscriber<T>) => void
  ): Observable<T> {
    const strKey = this.#hasher.hash(key);

    return new Observable((subscriber) => {
      this.addSubscriber(key, strKey, subscriber);

      subscribe(subscriber);

      return () => this.removeSubscriber(key, strKey, subscriber);
    });
  }

  /** Detaches all subscriptions. When 'complete' is true, all subscriptions will be sent a
   * 'complete' signal before being dropped. */
  detachAll(complete: boolean = true) {
    if (complete) {
      const sets = [...this.#observables.values()];
      this.#observables.clear();

      for (const set of sets) {
        for (const subscription of set) {
          subscription.complete();
        }
      }
    } else {
      this.#observables.clear();
    }
  }
}
